import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["disabled", "error", "fluid", "input", "label", "readOnly", "required", "size", "children"];
import PropTypes from 'prop-types';
import React, { cloneElement, forwardRef, useContext, useRef } from 'react';
import { identifier } from '../../util/identifier';
import { FormFieldContext } from '../FormField/FormField.context';
import { StyledLabeledInput } from './LabeledInput.styles';

/**
 * @related ['Input', 'Label']
 * @since 8.23.0
 */
export var LabeledInput = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var disabled = props.disabled,
    error = props.error,
    fluid = props.fluid,
    input = props.input,
    label = props.label,
    readOnly = props.readOnly,
    required = props.required,
    size = props.size,
    children = props.children,
    rest = _objectWithoutProperties(props, _excluded);
  var inputClone = /*#__PURE__*/cloneElement(input, Object.assign({
    fluid: fluid,
    size: 'lg',
    placeholder: input.props && input.props.placeholder ? input.props.placeholder : ' '
  }, rest));
  var context = Object.assign({}, useContext(FormFieldContext), {
    // Create a default id to be used with the input and labels
    controlId: useRef(identifier()).current
  });
  if (typeof disabled !== 'undefined') {
    context.disabled = disabled;
  }
  if (typeof error !== 'undefined') {
    context.error = error;
  }
  if (typeof readOnly !== 'undefined') {
    context.readOnly = readOnly;
  }
  if (typeof required !== 'undefined') {
    context.required = required;
  }

  // Note: selection control components should come first in the DOM tree so that
  // labels can trigger off :hover/:focus states.
  return /*#__PURE__*/React.createElement(StyledLabeledInput, {
    ref: forwardedRef
  }, /*#__PURE__*/React.createElement(FormFieldContext.Provider, {
    value: context
  }, inputClone, label));
});
LabeledInput.displayName = 'LabeledInput';
LabeledInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Displays the input in a disabled state. Can also be inherited from a FormField parent.
   */
  disabled: PropTypes.bool,
  /**
   * Displays the input in an error state. Can also be inherited from a FormField parent.
   */
  error: PropTypes.bool,
  /**
   * Inputs are fluid by default which means they stretch to fill the entire width of their
   * container. When `fluid="false"`, the inputs's width is set to `auto`.
   */
  fluid: PropTypes.bool,
  /**
   * A `Input` component.
   */
  input: PropTypes.node.isRequired,
  /**
   * A `Label` component.
   */
  label: PropTypes.node.isRequired,
  /**
   * Displays the input in a read-only state. Can also be inherited from a FormField parent.
   *
   * @since 8.27.0
   */
  readOnly: PropTypes.bool,
  /**
   * Indicates the input is required. Can also be inherited from a FormField parent.
   */
  required: PropTypes.bool
} : {};
LabeledInput.defaultProps = {
  fluid: true
};